import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { getEvents } from '../api/events';
import { deleteRule } from '../api/rules';
import { getCredentials } from '../api/client';
import { formatTimestamp, getStoredTimeFormat } from '../utils/timeFormat';
import { resolveEventType } from '../utils/resolveEventType';

interface SchemaItem {
  eventType: string;
  version: number;
  status: string;
}

interface RuleItem {
  name: string;
  ruleId?: string;
  sourceEventType: string;
  target: { type?: string; arn?: string; storeName?: string; targetEventType?: string };
}

interface IngressItem {
  type: string;
  name: string;
  targetEventTypes?: string[];
}

interface GraphInfoPanelProps {
  storeName: string;
  selectedNodeId: string | null;
  schemas: SchemaItem[];
  rules: RuleItem[];
  ingress?: IngressItem[];
  projectionConfig?: { reducerType: string; name: string };
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSelectNode: (nodeId: string) => void;
  onRegisterSchema: () => void;
  onAddRule: () => void;
}

export function GraphInfoPanel({
  storeName,
  selectedNodeId,
  schemas,
  rules,
  ingress,
  projectionConfig,
  onClose,
  onNavigate,
  onSelectNode,
  onRegisterSchema,
  onAddRule,
}: GraphInfoPanelProps) {
  if (!selectedNodeId) {
    return (
      <Container>
        <SpaceBetween size="m">
          <Box color="text-body-secondary" fontSize="body-s">
            Click a node to see details and associated rules.
          </Box>
          <SpaceBetween size="xs">
            <Button onClick={onRegisterSchema} iconName="add-plus">Register schema</Button>
            <Button onClick={onAddRule} iconName="add-plus">Add rule</Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    );
  }

  if (selectedNodeId.startsWith('ev:')) {
    return (
      <SchemaPanel
        storeName={storeName}
        eventType={selectedNodeId.slice(3)}
        schemas={schemas}
        rules={rules}
        onClose={onClose}
        onNavigate={onNavigate}
        onSelectNode={onSelectNode}
      />
    );
  }

  if (selectedNodeId.startsWith('cmp:')) {
    const ruleKey = selectedNodeId.slice(4);
    return (
      <RulePanel
        storeName={storeName}
        ruleKey={ruleKey}
        rules={rules}
        onClose={onClose}
        onNavigate={onNavigate}
        onSelectNode={onSelectNode}
      />
    );
  }

  if (selectedNodeId.startsWith('out:')) {
    const egressName = selectedNodeId.slice(4);
    return (
      <EgressPanel
        egressName={egressName}
        rules={rules}
        onClose={onClose}
        onNavigate={onNavigate}
        onSelectNode={onSelectNode}
      />
    );
  }

  if (selectedNodeId.startsWith('in:')) {
    const ingressName = selectedNodeId.slice(3);
    return (
      <IngressPanel
        ingressName={ingressName}
        ingress={ingress}
        onClose={onClose}
        onNavigate={onNavigate}
      />
    );
  }

  if (selectedNodeId.startsWith('proj:')) {
    return (
      <ProjectionPanel
        projectionConfig={projectionConfig}
        onClose={onClose}
      />
    );
  }

  return (
    <Container header={<Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>Node</Header>}>
      <Box>{selectedNodeId}</Box>
    </Container>
  );
}

function SchemaPanel({
  storeName,
  eventType,
  schemas,
  rules,
  onClose,
  onNavigate,
  onSelectNode,
}: {
  storeName: string;
  eventType: string;
  schemas: SchemaItem[];
  rules: RuleItem[];
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSelectNode: (nodeId: string) => void;
}) {
  const schema = schemas.find(s => s.eventType === eventType);
  const relatedRules = rules.filter(r => r.sourceEventType === eventType);

  const { data: recentData } = useQuery({
    queryKey: ['recent-events', storeName, eventType],
    queryFn: () => getEvents(storeName, '', { limit: 5, filter: `metadata.eventType=${eventType}`, sort: 'desc' }),
    enabled: !!getCredentials() && !!eventType,
    staleTime: 10000,
  });

  const recentEvents = ((recentData?.data as any)?.events || []) as any[];

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>
          Event Type
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div>
          <Box variant="awsui-key-label">Name</Box>
          <Box fontWeight="bold">{eventType}</Box>
        </div>
        {schema && (
          <>
            <div>
              <Box variant="awsui-key-label">Version</Box>
              <Box>{schema.version}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <StatusIndicator type={schema.status === 'ACTIVE' ? 'success' : 'stopped'}>
                {schema.status}
              </StatusIndicator>
            </div>
          </>
        )}

        {relatedRules.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Rules ({relatedRules.length})</Box>
            <SpaceBetween size="xxs">
              {relatedRules.map(r => (
                <Link
                  key={r.name}
                  fontSize="body-s"
                  onFollow={(e) => { e.preventDefault(); onSelectNode(`cmp:${r.name}`); }}
                >
                  → {r.name} ({r.target?.type || 'unknown'})
                </Link>
              ))}
            </SpaceBetween>
          </div>
        )}

        {recentEvents.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Recent events</Box>
            <SpaceBetween size="xxs">
              {recentEvents.map((evt: any, i: number) => {
                const ts = evt.metadata?.timestamp;
                const stream = evt.stream || '—';
                const version = evt.metadata?.version ?? evt.version ?? '?';
                return (
                  <Box key={evt.metadata?.eventId || i} fontSize="body-s" color="text-body-secondary">
                    v{version} · {stream.length > 16 ? stream.slice(0, 16) + '…' : stream} · {formatTimestamp(ts, getStoredTimeFormat())}
                  </Box>
                );
              })}
            </SpaceBetween>
          </div>
        )}

        <Button
          variant="link"
          onClick={() => onNavigate(`/stores/${storeName}?tab=events&filter=${encodeURIComponent(eventType)}`)}
        >
          View all in Ledger →
        </Button>
      </SpaceBetween>
    </Container>
  );
}

function RulePanel({
  storeName,
  ruleKey,
  rules,
  onClose,
  onNavigate,
  onSelectNode,
}: {
  storeName: string;
  ruleKey: string;
  rules: RuleItem[];
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSelectNode: (nodeId: string) => void;
}) {
  const queryClient = useQueryClient();
  const rule = rules.find(r => r.name === ruleKey);
  const targetType = (rule?.target?.type || '').toUpperCase();
  const targetEventType = rule?.target?.targetEventType;

  const deleteMutation = useMutation({
    mutationFn: () => deleteRule(storeName, rule?.ruleId || ruleKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules', storeName] });
      onClose();
    },
  });

  const { data: firingsData } = useQuery({
    queryKey: ['recent-firings', storeName, targetEventType],
    queryFn: () => getEvents(storeName, '', { limit: 5, filter: `metadata.eventType=${targetEventType}`, sort: 'desc' }),
    enabled: !!getCredentials() && !!targetEventType,
    staleTime: 10000,
  });

  const recentFirings = ((firingsData?.data as any)?.events || []) as any[];

  const lambdaName = rule?.target?.arn
    ? rule.target.arn.split(':function:').pop()?.split('/').pop() || rule.target.arn.split(':').pop()
    : undefined;

  const arnRegion = rule?.target?.arn?.split(':')[3] || 'us-east-2';
  const consoleLink = rule?.target?.arn && targetType === 'LAMBDA' && lambdaName
    ? `https://${arnRegion}.console.aws.amazon.com/lambda/home?region=${arnRegion}#/functions/${encodeURIComponent(lambdaName)}`
    : undefined;

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>
          Rule
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div>
          <Box variant="awsui-key-label">Name</Box>
          <Box fontWeight="bold">{rule?.name || ruleKey}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Type</Box>
          <Badge color={targetType === 'LAMBDA' ? 'blue' : targetType === 'BEDROCK' ? 'green' : 'grey'}>
            {targetType || 'UNKNOWN'}
          </Badge>
        </div>
        <div>
          <Box variant="awsui-key-label">Source event</Box>
          <Link
            fontSize="body-s"
            onFollow={(e) => { e.preventDefault(); onSelectNode(`ev:${rule?.sourceEventType}`); }}
          >
            {rule?.sourceEventType || '—'}
          </Link>
        </div>

        {targetType === 'LAMBDA' && lambdaName && (
          <div>
            <Box variant="awsui-key-label">Target Lambda</Box>
            <Box fontSize="body-s">λ {lambdaName}</Box>
          </div>
        )}

        {targetType === 'BEDROCK' && (
          <div>
            <Box variant="awsui-key-label">Model</Box>
            <Box fontSize="body-s">{rule?.target?.arn || 'Bedrock'}</Box>
          </div>
        )}

        {targetType === 'EVENTSTORE' && rule?.target?.storeName && (
          <div>
            <Box variant="awsui-key-label">Target store</Box>
            <Link
              fontSize="body-s"
              onFollow={(e) => { e.preventDefault(); onNavigate(`/stores/${rule.target.storeName}`); }}
            >
              {rule.target.storeName}
            </Link>
          </div>
        )}

        {targetEventType && (
          <div>
            <Box variant="awsui-key-label">Output event type</Box>
            <Box fontSize="body-s">{targetEventType}</Box>
          </div>
        )}

        <div>
          <Box variant="awsui-key-label">Status</Box>
          <StatusIndicator type="stopped">● No data (Phase 2B)</StatusIndicator>
        </div>

        {recentFirings.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Recent firings</Box>
            <SpaceBetween size="xxs">
              {recentFirings.map((evt: any, i: number) => {
                const ts = evt.metadata?.timestamp;
                const version = evt.metadata?.version ?? evt.version ?? '?';
                return (
                  <Box key={evt.metadata?.eventId || i} fontSize="body-s" color="text-body-secondary">
                    {resolveEventType(evt)} v{version} · {formatTimestamp(ts, getStoredTimeFormat())}
                  </Box>
                );
              })}
            </SpaceBetween>
          </div>
        )}

        {consoleLink && (
          <Link href={consoleLink} external fontSize="body-s">
            Open in Lambda console →
          </Link>
        )}

        <Button
          variant="normal"
          loading={deleteMutation.isPending}
          onClick={() => deleteMutation.mutate()}
        >
          Delete rule
        </Button>
      </SpaceBetween>
    </Container>
  );
}

function EgressPanel({
  egressName,
  rules,
  onClose,
  onNavigate,
  onSelectNode,
}: {
  egressName: string;
  rules: RuleItem[];
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSelectNode: (nodeId: string) => void;
}) {
  const isStore = !egressName.includes(':');
  const targetStore = isStore ? egressName : undefined;
  const forwardingRules = rules.filter(r =>
    (r.target?.type || '').toUpperCase() === 'EVENTSTORE' && r.target?.storeName === targetStore
  );

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>
          Egress
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div>
          <Box variant="awsui-key-label">Target</Box>
          <Box fontWeight="bold">{egressName}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Type</Box>
          <Badge>{isStore ? 'EventStore' : egressName.split(':')[0]?.toUpperCase()}</Badge>
        </div>

        {forwardingRules.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Forwarded by rules</Box>
            <SpaceBetween size="xxs">
              {forwardingRules.map(r => (
                <Link
                  key={r.name}
                  fontSize="body-s"
                  onFollow={(e) => { e.preventDefault(); onSelectNode(`cmp:${r.name}`); }}
                >
                  {r.name}
                </Link>
              ))}
            </SpaceBetween>
          </div>
        )}

        {targetStore && (
          <Button
            variant="link"
            onClick={() => onNavigate(`/stores/${targetStore}`)}
          >
            Open target store →
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}

function IngressPanel({
  ingressName,
  ingress,
  onClose,
  onNavigate,
}: {
  ingressName: string;
  ingress?: IngressItem[];
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const source = ingress?.find(s => s.name === ingressName);

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>
          Ingress
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div>
          <Box variant="awsui-key-label">Name</Box>
          <Box fontWeight="bold">{ingressName}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Type</Box>
          <Badge>{source?.type || 'Unknown'}</Badge>
        </div>
        {source?.targetEventTypes && source.targetEventTypes.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Accepted events</Box>
            <SpaceBetween size="xxs">
              {source.targetEventTypes.map(et => (
                <Box key={et} fontSize="body-s">{et}</Box>
              ))}
            </SpaceBetween>
          </div>
        )}
        {source?.type === 'EventStore' && (
          <Button
            variant="link"
            onClick={() => onNavigate(`/stores/${source.name}`)}
          >
            Open source store →
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}

function ProjectionPanel({
  projectionConfig,
  onClose,
}: {
  projectionConfig?: { reducerType: string; name: string };
  onClose: () => void;
}) {
  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close" />}>
          Projection
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div>
          <Box variant="awsui-key-label">Name</Box>
          <Box fontWeight="bold">{projectionConfig?.name || 'projection'}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Reducer</Box>
          <Box>{projectionConfig?.reducerType || 'MERGE_ALL'}</Box>
        </div>
      </SpaceBetween>
    </Container>
  );
}
